import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import SearchInput from '../Input/SearchInput';
import filterIcon from '../../../assets/images/filter.svg';

/**
 * The list-page card.
 *
 * WHAT IT IS
 *
 * Every list screen in the panel is the same object: a heading, a status tab
 * row, a search box with a filter and one primary action, a table, and a
 * pagination footer. Before this component each page hand-assembled that from
 * loose parts — the per-page select floated above the table on the left, the
 * pagination was a separate floating card underneath, and no two pages agreed
 * on the spacing between them.
 *
 * This is that object, in the arrangement the Figma list screens use: one
 * bordered panel holding all of it, with the table and the footer as bands
 * inside it rather than as separate cards.
 *
 * WHY THE PARTS ARE SLOTS
 *
 * `children` is the table itself, and `actions` is whatever button the page
 * puts beside search. Neither is prescribed, because the 125 screens that
 * render a table disagree about almost everything else — some have an export
 * button, some a dropdown, some nothing. What this component fixes is where
 * those things sit and how the card around them looks, not what they are.
 *
 * TABS ARE OPTIONAL
 *
 * `tabs` is a status facet — All / Active / Disabled on Users, All / Active /
 * Expired / Draft on Challenges. A screen with no meaningful facet passes
 * none and the row is not rendered, rather than showing a lone "All".
 */

const TableTabs = ({ tabs, value, onChange }) => (
  <div className="flex gap-6 overflow-x-auto hide-scrollbar">
    {tabs.map((tab) => {
      const active = tab.value === value;
      return (
        <button
          key={String(tab.value)}
          type="button"
          onClick={() => onChange(tab.value)}
          className={`shrink-0 pb-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
            active
              ? 'border-shoorah-lightBlack dark:border-white text-shoorah-lightBlack dark:text-white'
              : 'border-transparent text-shoorah-gray3 hover:text-shoorah-lightBlack dark:hover:text-white'
          }`}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

TableTabs.propTypes = {
  tabs: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired
};

/**
 * The footer: which rows are on screen, the page controls, and how many rows
 * a page holds.
 *
 * The per-page select lives here rather than above the table because that is
 * the only place it is read — an admin changes it after seeing the list is
 * too long, not before.
 */
export const TablePagination = ({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  disabled = false
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const first = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const last = Math.min(currentPage * pageSize, totalCount);

  /** At most five numbers, centred on the current page where there is room. */
  const pages = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.min(Math.max(currentPage - 2, 1), totalPages - 4);
    return Array.from({ length: 5 }, (_, i) => start + i);
  })();

  const go = (page) => {
    if (disabled || page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const step = 'text-sm text-shoorah-lightBlack dark:text-shoorah-darkBg disabled:opacity-40 px-1';

  return (
    <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder rounded-btn">
      <p className="text-sm text-shoorah-lightBlack dark:text-white">
        {first}-{last} of {totalCount} items
      </p>

      <div className="flex items-center gap-1 flex-wrap justify-center">
        <button type="button" className={step} onClick={() => go(1)} disabled={currentPage === 1}>
          &laquo; Prev
        </button>
        <button
          type="button"
          className={step}
          onClick={() => go(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lsaquo; Prev
        </button>

        {pages.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => go(page)}
            aria-current={page === currentPage ? 'page' : undefined}
            className={`min-w-[28px] h-7 rounded-md text-sm transition-colors ${
              page === currentPage
                ? 'font-semibold text-shoorah-lightBlack dark:text-white'
                : 'text-shoorah-gray3 hover:text-shoorah-lightBlack dark:hover:text-white'
            }`}
          >
            {page}
          </button>
        ))}

        <button
          type="button"
          className={step}
          onClick={() => go(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next &rsaquo;
        </button>
        <button
          type="button"
          className={step}
          onClick={() => go(totalPages)}
          disabled={currentPage === totalPages}
        >
          Next &raquo;
        </button>
      </div>

      {onPageSizeChange ? (
        <div className="flex items-center gap-2">
          {/*
            A Listbox rather than a native <select>: the browser draws a
            native option list from the OS theme, which on a dark desktop
            came out as grey-on-grey regardless of what the page asked for.
            This one is ours to style, so it matches the card in both themes.
          */}
          <Listbox value={pageSize} onChange={onPageSizeChange}>
            <div className="relative">
              <Listbox.Button className="h-8 ps-3 pe-2 flex items-center gap-1 rounded-md border border-shoorah-borderGray dark:border-shoorah-darkBorder text-sm text-shoorah-lightBlack dark:text-white focus:outline-none">
                {pageSize}
                <ChevronDownIcon className="w-4 h-4" />
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute right-0 bottom-full mb-2 z-20 min-w-[5rem] py-1 rounded-btn bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder shadow-lg focus:outline-none">
                  {pageSizeOptions.map((size) => (
                    <Listbox.Option
                      key={size}
                      value={size}
                      className={({ active }) =>
                        `flex items-center gap-2 px-3 py-2 text-sm cursor-pointer text-shoorah-lightBlack dark:text-white ${
                          active ? 'bg-shoorah-normanGray dark:bg-shoorah-darkBgColor' : ''
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <CheckIcon className={`w-4 h-4 ${selected ? '' : 'invisible'}`} />
                          {size}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          <span className="text-sm text-shoorah-lightBlack dark:text-white whitespace-nowrap">
            Items per page
          </span>
        </div>
      ) : null}
    </div>
  );
};

TablePagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  disabled: PropTypes.bool
};

const TableCard = ({
  title,
  description,
  headerAction,
  tabs = [],
  activeTab,
  onTabChange,
  search,
  onFilter,
  filterCount = 0,
  actions,
  children,
  pagination,
  className = ''
}) => (
  <section
    className={`bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder rounded-card ${className}`}
  >
    {(title || description || headerAction) && (
      <div className="px-4 pt-5 sm:px-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          {title && (
            <h2 className="text-xl font-medium text-shoorah-lightBlack dark:text-white">{title}</h2>
          )}
          {description && <p className="text-sm text-shoorah-gray3 mt-1">{description}</p>}
        </div>
        {/*
          Sits opposite the title rather than in the control row below, because
          it switches what the whole card is showing — it is not another filter
          on the current view.
        */}
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </div>
    )}

    <div className="px-4 sm:px-6 pt-5 flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
      {tabs.length > 0 ? (
        <TableTabs tabs={tabs} value={activeTab} onChange={onTabChange} />
      ) : (
        <span />
      )}

      {/*
        On a phone the three controls stack: search takes the full width,
        then Filter and the action button share the row below it. SearchInput
        carries its own fixed widths, so the wrapper sets the width and the
        field is told to fill it.
      */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:justify-end w-full lg:w-auto">
        {search ? (
          <div className="w-full sm:w-[260px] lg:w-[300px]">
            <SearchInput
              id={search.id || 'searchKey'}
              name={search.name || 'searchKey'}
              type="text"
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder || 'Search'}
              inputClassName="!w-full"
            />
          </div>
        ) : null}

        {(onFilter || actions) && (
          <div className="flex items-center gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
            {onFilter ? (
              <button type="button" className="btn-secondary" onClick={onFilter}>
                <img src={filterIcon} alt="" className="w-4 h-4 dark:invert" />
                Filter
                {filterCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-shoorah-active text-white">
                    {filterCount}
                  </span>
                )}
              </button>
            ) : null}

            {actions}
          </div>
        )}
      </div>
    </div>

    <div className="mt-5 px-4 sm:px-6">{children}</div>

    {pagination ? (
      <div className="px-4 sm:px-6 pt-4 pb-5">
        <TablePagination {...pagination} />
      </div>
    ) : null}
  </section>
);

TableCard.propTypes = {
  /** Control sitting opposite the title — a view switch, not a list control. */
  headerAction: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  /** [{ label, value }] — a status facet, not navigation. */
  tabs: PropTypes.array,
  activeTab: PropTypes.any,
  onTabChange: PropTypes.func,
  /** { value, onChange, placeholder } — omit for a list with nothing to search. */
  search: PropTypes.object,
  onFilter: PropTypes.func,
  filterCount: PropTypes.number,
  actions: PropTypes.node,
  children: PropTypes.node,
  /** Props for TablePagination; omit on a list that never paginates. */
  pagination: PropTypes.object,
  className: PropTypes.string
};

export default TableCard;
